/**
 * 仅在 Capacitor iOS 原生包使用：系统相册/相机选图，产出与 input[type=file] 一致的 File，供现有上传链路复用。
 * Android 与所有浏览器不调用本模块任何方法（由调用方 isCapacitorIosNative 判断）。
 */
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isCapacitorIosNative } from './capacitorIosNative';

export { isCapacitorIosNative };

/**
 * 中心正方形裁剪为 JPEG File，便于与现有 compressImage / 上传逻辑衔接。
 */
function squareCenterCropToJpegFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => {
      const img = new Image();
      img.onload = () => {
        const w = img.width;
        const h = img.height;
        const side = Math.min(w, h);
        const sx = (w - side) / 2;
        const sy = (h - side) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = side;
        canvas.height = side;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, side, side, 0, 0, side, side);
        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('头像裁剪失败'));
              return;
            }
            resolve(
              new File([blob], 'avatar.jpg', {
                type: 'image/jpeg',
                lastModified: Date.now(),
              })
            );
          },
          'image/jpeg',
          0.92
        );
      };
      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = event.target.result;
    };
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });
}

function isUserCancelled(err) {
  const m = `${err?.message || ''} ${err?.code || ''}`.toLowerCase();
  return (
    m.includes('cancel') ||
    m.includes('canceled') ||
    m.includes('cancelled') ||
    m.includes('user cancel')
  );
}

function isPermissionError(err) {
  const m = `${err?.message || ''}`.toLowerCase();
  return (
    m.includes('permission') ||
    m.includes('denied') ||
    m.includes('not authorized') ||
    (m.includes('access') && m.includes('photo'))
  );
}

/**
 * 弹出系统「从相册选择 / 拍照」；选图后返回正方形 JPEG File。
 * 用户取消返回 null；权限等问题 throw Error（文案已友好化）。
 */
export async function pickAvatarImageViaCapacitorCamera() {
  try {
    await Camera.requestPermissions({ permissions: ['camera', 'photos'] });
  } catch (_) {
    /* 仍继续：后续 getPhoto 会触发系统授权框；失败时在 catch 中提示 */
  }

  let image;
  try {
    image = await Camera.getPhoto({
      quality: 92,
      // iOS：拍照可走系统裁剪；相册选图在部分系统上无编辑界面，下方统一做正方形裁剪
      allowEditing: true,
      resultType: CameraResultType.Base64,
      source: CameraSource.Prompt,
      correctOrientation: true,
      promptLabelHeader: '选择头像',
      promptLabelCancel: '取消',
      promptLabelPhoto: '从相册选择',
      promptLabelPicture: '拍照',
    });
  } catch (err) {
    if (isUserCancelled(err)) return null;
    if (isPermissionError(err)) {
      throw new Error(
        '请在 iPhone「设置 → 隐私与安全性 → 相机 / 照片」中允许「悟空听说」访问，以便更换头像'
      );
    }
    throw new Error(err?.message || '选取图片失败，请重试');
  }

  if (!image?.base64String) {
    return null;
  }

  const fmt = (image.format || 'jpeg').toLowerCase();
  const mime = fmt === 'png' ? 'image/png' : 'image/jpeg';
  const ext = fmt === 'png' ? 'png' : 'jpg';
  const dataUrl = `data:${mime};base64,${image.base64String}`;
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const rawFile = new File([blob], `avatar.${ext}`, { type: mime, lastModified: Date.now() });

  if (rawFile.size > 5 * 1024 * 1024) {
    throw new Error('图片大小不能超过 5MB');
  }

  return squareCenterCropToJpegFile(rawFile);
}
